
import { ContactFormData, ContactFormErrors } from './contactFormTypes';

interface ValidationResult {
  valid: boolean;
  errors: ContactFormErrors;
}

export const validateContactForm = (data: ContactFormData): ValidationResult => {
  const errors: ContactFormErrors = {
    name: '',
    email: '',
    consent: ''
  };
  
  let valid = true;
  
  // Name validation - prevent XSS and ensure proper length
  if (!data.name || data.name.trim().length === 0) {
    errors.name = 'Name is required';
    valid = false;
  } else if (data.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
    valid = false;
  } else if (data.name.trim().length > 100) {
    errors.name = 'Name must be less than 100 characters';
    valid = false;
  } else if (/[<>]/.test(data.name)) {
    errors.name = 'Name contains invalid characters';
    valid = false;
  }
  
  // Email validation with more comprehensive regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!data.email || data.email.trim().length === 0) {
    errors.email = 'Email address is required';
    valid = false;
  } else if (!emailRegex.test(data.email)) {
    errors.email = 'Please enter a valid email address';
    valid = false;
  } else if (data.email.length > 254) {
    errors.email = 'Email address is too long';
    valid = false;
  }
  
  // Phone validation (optional)
  if (data.phone && data.phone.trim().length > 0) {
    // More flexible phone regex that accepts international formats
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(data.phone) && data.phone.trim().length > 0) {
      errors.phone = 'Please enter a valid phone number';
      valid = false;
    }
  }
  
  // Message validation (optional)
  if (data.message && data.message.trim().length > 1000) {
    errors.message = 'Message must be less than 1000 characters';
    valid = false;
  }
  
  // Consent validation
  if (!data.consent) {
    errors.consent = 'You must agree to receive updates';
    valid = false;
  }
  
  return {
    valid,
    errors
  };
};

// Utility function for real-time validation of a single field
export const validateField = (
  name: keyof ContactFormData, 
  value: string | boolean
): string => {
  switch (name) {
    case 'name':
      if (typeof value === 'string') {
        if (!value || value.trim().length === 0) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (value.trim().length > 100) return 'Name must be less than 100 characters';
        if (/[<>]/.test(value)) return 'Name contains invalid characters';
      }
      return '';
    
    case 'email':
      if (typeof value === 'string') {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!value || value.trim().length === 0) return 'Email address is required';
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        if (value.length > 254) return 'Email address is too long';
      }
      return '';
    
    case 'phone':
      if (typeof value === 'string' && value && value.trim().length > 0) {
        const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/;
        if (!phoneRegex.test(value)) return 'Please enter a valid phone number';
      }
      return '';
    
    case 'message':
      if (typeof value === 'string' && value && value.trim().length > 1000) {
        return 'Message must be less than 1000 characters';
      }
      return '';
    
    case 'consent':
      if (typeof value === 'boolean' && !value) {
        return 'You must agree to receive updates';
      }
      return '';
    
    default:
      return '';
  }
};
